/**
 * Daily checklist completion guard for unresolved forced acknowledgements (WC-X2).
 * Shared by server (`salesDailyChecklist.ts`) and client (`useSalesDailyChecklistWidget`).
 */

export type AckBlockingRow = {
  requires_ack?: boolean | null;
  acknowledged_at?: string | null;
  archived_at?: string | null;
  ack_deadline_at?: string | null;
};

/** True when this notification row should count toward checklist ack blocking. */
export function notificationCountsAsBlockingAck(
  row: AckBlockingRow,
  nowMs: number = Date.now()
): boolean {
  if (row.requires_ack !== true) return false;
  if (row.acknowledged_at) return false;
  if (row.archived_at) return false;
  if (row.ack_deadline_at) {
    const deadlineMs = Date.parse(row.ack_deadline_at);
    if (!Number.isNaN(deadlineMs) && deadlineMs < nowMs) return false;
  }
  return true;
}

export function countBlockingRequiredAcks(
  rows: readonly AckBlockingRow[],
  nowMs: number = Date.now()
): number {
  return rows.filter((row) => notificationCountsAsBlockingAck(row, nowMs)).length;
}

export function ackBlocksChecklistCompletion(input: {
  ackBlockingEnabled: boolean;
  pendingRequiredAckCount: number;
}): boolean {
  return input.ackBlockingEnabled && input.pendingRequiredAckCount > 0;
}

/** Whether the daily checklist is fully complete (task units + optional ack gate). */
export function computeChecklistAllDone(input: {
  unitsTotal: number;
  unitsDone: number;
  ackBlockingEnabled: boolean;
  pendingRequiredAckCount: number;
}): boolean {
  const tasksComplete = input.unitsTotal > 0 && input.unitsDone >= input.unitsTotal;
  if (!tasksComplete) return false;
  return !ackBlocksChecklistCompletion(input);
}

/** Client helper after capability-adjusted fraction is applied. */
export function resolveChecklistAllDone(input: {
  fraction: { done: number; total: number };
  ackBlockingEnabled: boolean;
  pendingRequiredAckCount: number;
}): boolean {
  return computeChecklistAllDone({
    unitsTotal: input.fraction.total,
    unitsDone: input.fraction.done,
    ackBlockingEnabled: input.ackBlockingEnabled,
    pendingRequiredAckCount: input.pendingRequiredAckCount,
  });
}
